import { focusManager, QueryClient } from '@tanstack/react-query'

import { showToast } from '@/utils/toastMessage'

/**
 * Configure focus manager to detect tab visibility changes
 * By default, React Query only detects window focus, not tab switches
 */
focusManager.setEventListener((handleFocus) => {
  if (typeof window !== 'undefined' && window.addEventListener) {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleFocus()
      }
    }

    window.addEventListener('visibilitychange', onVisibilityChange, false)
    // @ts-expect-error - TS doesn't recognize focus event on window
    window.addEventListener('focus', handleFocus, false)

    return () => {
      window.removeEventListener('visibilitychange', onVisibilityChange)
      // @ts-expect-error - TS doesn't recognize focus event on window
      window.removeEventListener('focus', handleFocus)
    }
  }
})

/**
 * Global QueryClient configuration for React Query
 *
 * Default options:
 * - staleTime: 5 minutes - how long data is considered fresh
 * - gcTime: 10 minutes - how long unused data stays in cache (formerly cacheTime)
 * - retry: 2 attempts for queries, 1 for mutations
 * - refetchOnWindowFocus: true - refetch when user returns to tab
 * - refetchOnReconnect: true - refetch when internet reconnects
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (garbage collection time)
      retry: (failureCount, error: any) => {
        // Handle timeout errors specifically
        if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
          // Show user-friendly timeout message instead of triggering error boundary
          if (failureCount === 0) { // Only show toast on first retry attempt
            showToast({
              severity: 'warn',
              summary: 'Verbindungsproblem',
              detail: 'Die Anfrage hat zu lange gedauert. Versuche es erneut...',
            })
          }
          // Retry timeouts once more with longer delay
          return failureCount < 1
        }

        // Only retry GET requests
        const method = error?.config?.method?.toUpperCase() || 'GET'
        if (method !== 'GET') {
          return false
        }

        // Don't retry more than 3 times
        if (failureCount >= 3) return false

        // Retry on network errors and specific browser errors
        if (
          error?.code === 'ECONNABORTED' ||
          error?.code === 'ENOTFOUND' ||
          error?.code === 'ECONNREFUSED' ||
          error?.code === 'EPIPE' ||
          error?.code === 'ECONNRESET' ||
          error?.message?.includes('NS_BINDING_ERROR') ||
          error?.message?.includes('Network Error') ||
          error?.message?.includes('Failed to fetch')
        ) {
          return true
        }

        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false
        }

        // Retry on 5xx errors (server errors)
        if (error?.response?.status >= 500) {
          return true
        }

        // Default retry logic (2 retries)
        return failureCount < 2
      },
      refetchOnWindowFocus: true, // Refetch when window regains focus
      refetchOnReconnect: true, // Refetch when internet reconnects
      refetchOnMount: true, // Refetch when component mounts if data is stale
    },
    mutations: {
      retry: 0, // Never retry mutations to prevent duplicate operations
      onError: (error: any) => {
        // Handle timeout errors specifically for mutations
        if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
          showToast({
            severity: 'error',
            summary: 'Zeitüberschreitung',
            detail: 'Die Anfrage hat zu lange gedauert. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
          })
          return // Don't log timeout errors as they are handled gracefully
        }
        // Global error handler for other mutations
        console.error('Mutation error:', error)
      },
    },
  },
})

// This code is only for TypeScript
declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: import('@tanstack/react-query').QueryClient
  }
}

// This code is for all users
window.__TANSTACK_QUERY_CLIENT__ = queryClient
